import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  Button,
  Chip,
  Dropdown,
  DropdownOption,
  Fade,
  Icon,
  Table,
  TableColumn,
  TableVariant,
  Tooltip,
} from '@column/column-ui-kit';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { SectionHeader } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { LogoLoading } from '~/elements';
import { DashboardUserRedacted, FactorTypeResponse, Invite, InviteRepository, UserRepository } from '~/repositories';
import {
  OwnerRoleId,
  RoleRepository,
  getRoleLabel,
  precannedRolesByID,
  precannedRolesByName,
} from '~/repositories/RoleRepository';
import { Factor } from '~/repositories/UserRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';

import { InviteUsersModal } from './_components/InviteUsersModal';

interface UserListInfo {
  id: string;
  name: string;
  isOwner: boolean;
}

interface UserListRole {
  id: string;
  name: string;
}

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const RoleHelpButton = styled(Button)`
  padding: 0;
`;

const INVITE_REQUIRES_MFA_TOOLTIP = 'Enable two-factor authentication in your profile to invite users.';

interface UserRow {
  id: string;
  info: UserListInfo;
  email: string;
  factors: Factor[];
  role: UserListRole;
  user: DashboardUserRedacted;
}

interface InviteRow {
  id: string;
  email: string;
  platformRole: string;
  expiredAt?: Date;
  created?: Date;
  invite: Invite;
}

const usersQueryKey = (platformId?: string) => ['users', 'platform', platformId];
const invitesQueryKey = (platformId?: string) => ['invites', 'platform', platformId];
const rolesQueryKey = () => ['roles'];

export const PageTeam: React.FC = () => {
  const { currentUser, currentPlatform, currentPermission } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const openInviteModal = useCallback(() => setIsInviteModalOpen(true), []);
  const closeInviteModal = useCallback(() => setIsInviteModalOpen(false), []);

  const hasMfaEnabled = useMemo(
    () =>
      currentUser?.factors?.some(
        (factor) =>
          factor.factorVerified &&
          (factor.factorType === FactorTypeResponse.TOTP || factor.factorType === FactorTypeResponse.SMS)
      ) ?? false,
    [currentUser]
  );

  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    queryKey: usersQueryKey(currentPlatform?.id),
    queryFn: async () => {
      if (!currentPlatform) throw new Error('No platform selected');
      return UserRepository.getAll({ platformId: currentPlatform.id });
    },
    enabled: !!currentPlatform,
  });

  const { data: invitesResponse, isLoading: isInvitesLoading } = useQuery({
    queryKey: invitesQueryKey(currentPlatform?.id),
    queryFn: async () => {
      if (!currentPlatform) throw new Error('No platform selected');
      return InviteRepository.getAll({ platformId: currentPlatform.id });
    },
    enabled: !!currentPlatform,
  });

  const isLoading = isUsersLoading || isInvitesLoading;
  const invites = invitesResponse?.invites ?? [];

  const { data: rolesResponse } = useQuery({
    queryKey: rolesQueryKey(),
    queryFn: async () => RoleRepository.list(),
  });

  const rolesList: DropdownOption[] = useMemo(() => {
    return (rolesResponse?.roles || []).map((role) => ({
      label: getRoleLabel(role.name),
      value: role.id,
    }));
  }, [rolesResponse]);

  const deleteInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return InviteRepository.delete(currentPlatform.id, email);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitesQueryKey(currentPlatform?.id) });
      addSuccessNotification({
        content: 'Invite deleted',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to delete invite',
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return InviteRepository.resend(email, currentPlatform.id);
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Invite resent',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to resend invite',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return UserRepository.updatePlatformRole(currentPlatform.id, {
        userId,
        customRoleId: precannedRolesByID[roleId] ? undefined : roleId,
        precannedRoleName: precannedRolesByID[roleId] ? precannedRolesByID[roleId].name : undefined,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey(currentPlatform?.id) });
      const role = rolesResponse?.roles?.find((r) => r.id === variables.roleId);
      if (role) {
        addSuccessNotification({
          content: `Role updated to "${getRoleLabel(role.name)}" successfully.`,
        });
      }
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to update role',
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async ({ user, platformId }: { user: DashboardUserRedacted; platformId: string }) => {
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: user.id,
        platformId,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey(currentPlatform?.id) });
      addSuccessNotification({
        content: `"${variables.user.firstName} ${variables.user.lastName}" successfully removed from this platform.`,
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to remove user',
      });
    },
  });

  const deleteFactorMutation = useMutation({
    mutationFn: async ({ factorId }: { factorId: string }) => {
      return UserRepository.deleteFactor({ factorId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey(currentPlatform?.id) });
      addSuccessNotification({
        content: 'Authenticator app disabled',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to disable authenticator',
      });
    },
  });

  const handleRoleChange = useCallback(
    (userId: string, email: string, roleId: string) => {
      if (!currentPlatform || !rolesResponse) return;
      updateRoleMutation.mutate({ userId, roleId });
    },
    [currentPlatform, rolesResponse, updateRoleMutation]
  );

  const handleRemoveUser = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      removeUserMutation.mutate({ user, platformId });
    },
    [removeUserMutation]
  );

  const handleUserRemoveConfirm = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      openAlert({
        headline: `Remove ${user.firstName}?`,
        content: `Are you sure you want to remove ${user.firstName} ${user.lastName} from this platform?`,
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => handleRemoveUser(user, platformId),
      });
    },
    [handleRemoveUser, openAlert]
  );

  const handleUserDisableTotpMfa = useCallback(
    (user: DashboardUserRedacted, totpFactor: Factor) => {
      openAlert({
        headline: `Disable Authenticator App for ${user.firstName}`,
        content: `This action cannot be undone. If authenticator app two-factor authentication is disabled, it can
          only be re-enabled by ${user.firstName}.`,
        confirmButton: {
          label: 'Disable',
          variant: 'danger',
        },
        onSuccess: () => {
          deleteFactorMutation.mutate({ factorId: totpFactor.factorId });
        },
      });
    },
    [deleteFactorMutation, openAlert]
  );

  const deleteInvite = useCallback(
    (email: string) => {
      deleteInviteMutation.mutate(email);
    },
    [deleteInviteMutation]
  );

  const resendInvite = useCallback(
    (email: string) => {
      resendInviteMutation.mutate(email);
    },
    [resendInviteMutation]
  );

  const userList: UserRow[] = useMemo(() => {
    if (!currentUser || !usersResponse) return [];
    return usersResponse.dashboardUsers.map((user) => ({
      id: user.id,
      info: {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        isOwner: user.platformRole.name === 'owner',
      },
      email: user.email,
      factors: user.factors,
      role: {
        id: user.platformRole.id,
        name: user.platformRole.name,
      },
      user,
    }));
  }, [currentUser, usersResponse]);

  const userColumns: TableColumn<UserRow>[] = useMemo(
    () =>
      currentPlatform
        ? [
            {
              header: 'Name',
              accessor: 'info',
              width: { min: 300, max: '1fr' },
              cell: ({ value }) => (
                <Table.TextCell value={value.name} weight="medium">
                  <Flex gap={4}>
                    {(value.id === currentUser?.id || value.isOwner) && (
                      <>
                        {value.id === currentUser?.id && <Chip type="info">You</Chip>}
                        {value.isOwner && <Chip>Owner</Chip>}
                      </>
                    )}
                  </Flex>
                </Table.TextCell>
              ),
            },
            {
              header: 'Email',
              accessor: 'email',
              width: { min: 'min-content', max: '1fr' },
              cell: ({ value }) => <Table.TextCell value={value} />,
            },
            {
              header: '2FA',
              accessor: 'factors',
              width: { min: 'min-content', max: '1fr' },
              cell: ({ value }) => {
                const factors = value ?? [];
                if (factors.length === 0) {
                  return <Table.ChipCell value="Not enabled" />;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP)) {
                  return <Table.ChipCell value="Authenticator" type="success" />;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS)) {
                  return <Table.ChipCell value="SMS" type="success" />;
                }
                return <Table.Cell />;
              },
            },
            {
              header: (
                <RoleHelpButton
                  icon={<Icon.CircleQuestionmark />}
                  onClick={() => openHelpSidebar('Roles')}
                  variant="subtle"
                  size="small"
                >
                  Role
                </RoleHelpButton>
              ),
              accessor: 'role',
              width: { min: 200, max: 300 },
              cell: ({ row, value }) => {
                const activeRoleId = value.id === OwnerRoleId ? precannedRolesByName.admin.id : value.id;
                const isDisabled =
                  (currentPermission?.platformInfo !== 'write' &&
                    currentPlatform.id === currentUser?.defaultPlatformId) ||
                  currentUser?.id === value.id ||
                  (value.id === OwnerRoleId && currentPlatform.id === currentUser?.defaultPlatformId);
                return (
                  <Table.Cell>
                    <Select
                      value={activeRoleId}
                      onValueChange={(roleId) => handleRoleChange(row.id, row.email, roleId)}
                      disabled={isDisabled}
                    >
                      <SelectTrigger size="sm" variant="outline" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rolesList.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Table.Cell>
                );
              },
            },
          ]
        : [],
    [currentPlatform, currentUser, currentPermission, rolesList, handleRoleChange, openHelpSidebar]
  );

  const userRowAction = useCallback(
    (row: UserRow) => {
      const smsFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS
      );
      const totpFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP
      );

      return (
        <Dropdown
          isInside
          positionRight
          options={[
            {
              label: 'Reset Authenticator 2FA',
              isDisabled: !smsFactor || !totpFactor,
              tooltip: !totpFactor
                ? { content: 'User does not have Authenticator 2FA configured', placement: 'left' }
                : !smsFactor
                  ? { content: 'Authenticator 2FA may not be reset without SMS 2FA enabled', placement: 'left' }
                  : undefined,
              onClick: totpFactor ? () => handleUserDisableTotpMfa(row.user, totpFactor) : undefined,
            },
            {
              label: 'Remove',
              isDanger: true,
              isDisabled: currentUser?.id === row.user.id || row.user.platformRole.name === 'owner',
              onClick: () => handleUserRemoveConfirm(row.user, row.user.platformRole.platformId),
            },
          ]}
          variant="dots"
        />
      );
    },
    [currentUser, handleUserDisableTotpMfa, handleUserRemoveConfirm]
  );

  const inviteList: InviteRow[] = useMemo(
    () =>
      invites.map((invite) => ({
        id: invite.platformId,
        email: invite.email,
        platformRole: invite.platformRole,
        expiredAt: invite.expiredAt ? new Date(invite.expiredAt) : undefined,
        created: invite.createdAt ? new Date(invite.createdAt) : undefined,
        invite,
      })),
    [invites]
  );

  const inviteColumns: TableColumn<InviteRow>[] = useMemo(
    () => [
      {
        header: 'Email',
        accessor: 'email',
        width: { min: 300, max: '1fr' },
        cell: ({ value }) => <Table.TextCell value={value} weight="medium" />,
      },
      {
        accessor: 'expiredAt',
        width: { min: 'min-content', max: '1fr' },
        cell: ({ value }) => (
          <Table.Cell>{value && value < new Date() ? <Chip type="danger">Expired</Chip> : null}</Table.Cell>
        ),
      },
      {
        header: (
          <RoleHelpButton
            icon={<Icon.CircleQuestionmark />}
            onClick={() => openHelpSidebar('Roles')}
            variant="subtle"
            size="small"
          >
            Role
          </RoleHelpButton>
        ),
        accessor: 'platformRole',
        width: { min: 100, max: 116 },
        cell: ({ value }) => <Table.ChipCell value={getRoleLabel(value)} />,
      },
      {
        header: 'Created',
        accessor: 'created',
        width: { min: 'min-content', max: '1fr' },
        cell: ({ value }) => (value ? <Table.TimeCell value={value.toISOString()} /> : <Table.Cell />),
      },
    ],
    [openHelpSidebar]
  );

  const inviteRowAction = useCallback(
    (row: InviteRow) => (
      <Dropdown
        isInside
        positionRight
        options={[
          {
            label: 'Resend',
            onClick: () => resendInvite(row.email),
          },
          {
            label: 'Delete',
            onClick: () => deleteInvite(row.email),
            isDanger: true,
          },
        ]}
        variant="dots"
      />
    ),
    [deleteInvite, resendInvite]
  );

  return (
    <>
      <InviteUsersModal
        onClose={closeInviteModal}
        onSave={() => {
          closeInviteModal();
          queryClient.invalidateQueries({ queryKey: invitesQueryKey(currentPlatform?.id) });
        }}
        open={isInviteModalOpen}
        roles={rolesList}
      />
      <PageActions>
        <Tooltip
          content={INVITE_REQUIRES_MFA_TOOLTIP}
          isOpen={hasMfaEnabled || !currentUser ? false : undefined}
          placement="left"
        >
          <Button onClick={openInviteModal} icon={<Icon.Mail />} size="small" isDisabled={!hasMfaEnabled}>
            Invite User
          </Button>
        </Tooltip>
      </PageActions>
      <StyledFade show={isLoading}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!isLoading}>
        <SectionHeader text="Users" />
        <Inner pt={0} px="var(--page-margin)">
          <Table
            columns={userColumns}
            data={userList}
            isLoading={isUsersLoading}
            variant={TableVariant.List}
            getRowId={(row) => row.id}
            rowAction={userRowAction}
          />
        </Inner>
        <SectionHeader text="Pending Invites" border />
        <Inner pt={0} px="var(--page-margin)">
          <Table
            columns={inviteColumns}
            data={inviteList}
            isLoading={isInvitesLoading}
            variant={TableVariant.List}
            getRowId={(row) => row.id}
            rowAction={inviteRowAction}
          />
        </Inner>
      </Fade>
    </>
  );
};
